import { useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  Button,
  Switch,
  Checkbox,
  FormControlLabel,
  Grid,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { useAppDispatch } from '../../../../redux/hooks';
import { grpDetailsActions } from '../../../../redux/slices/group-details/groupDetails';
import { InputField } from '../Program/components/program tabs/ProgramSettings/components/SettingsUI';
import { LoadingSpinner } from '../Program/components/program tabs/ProgramSettings/components/LoadingSpinner';
import { useSettingsLayoutState } from '../Program/components/program tabs/ProgramSettings/hooks/useSettingsLayoutState';
import { useSettingsLayoutSave } from '../Program/components/program tabs/ProgramSettings/hooks/useSettingsLayoutSave';
import { AI_MATCHING_FIELD_OPTIONS, ALWAYS_ON_AI_FIELD_LABEL } from './aiMatchingFields';
import { useMemberWord } from '../../../../utils/memberTypeLabels';

interface MatchSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Match Settings drawer for the orgLevelAdmin Matches page (Mentorship / Guided).
 * Surfaces only the matchmaking-relevant config — Learning Objectives + the
 * Mentorship matching rules + AI Matching — reusing the exact SettingsLayout
 * state/save hooks (so fields + persistence are identical to the full settings
 * surface). Scrollable body with a fixed Save footer.
 */
const MatchSettingsDrawer = ({ open, onClose }: MatchSettingsDrawerProps) => {
  const dispatch = useAppDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const w = useMemberWord();

  // Bump refetchKey when the drawer opens so LO's + group reload fresh.
  const state = useSettingsLayoutState('configuration', open ? 1 : 0);
  const {
    grpId,
    orgId,
    location,
    groupData,
    model,
    programName,
    participantLabel,
    advisorLabel,
    maxMatchesPerMentee,
    setMaxMatchesPerMentee,
    maxMatchesPerMentor,
    setMaxMatchesPerMentor,
    sessionsPerMatch,
    setSessionsPerMatch,
    useAIMatching,
    setUseAIMatching,
    aiMatchingFields,
    setAiMatchingField,
    learningObjectives,
    setLearningObjectives,
    fieldSettings,
    subjects,
    basicSettingsLoading,
    learningObjectivesLoading,
    hasPendingChanges,
    currentSnapshot,
    setLastSavedSnapshot,
    isDesignDirty,
    setIsDesignDirty,
    markUserEdited,
    refetchGroupDetails
  } = state;

  const { executeSave } = useSettingsLayoutSave({
    grpId: grpId || '',
    orgId: orgId || '',
    location: location || '',
    view: 'configuration',
    hasPendingChanges,
    isDesignDirty,
    programName,
    model,
    participantLabel,
    advisorLabel,
    maxMatchesPerMentee,
    maxMatchesPerMentor,
    sessionsPerMatch,
    useAIMatching,
    aiMatchingFields,
    fieldSettings,
    subjects,
    learningObjectives,
    currentSnapshot,
    groupData,
    refetchGroupDetails,
    setLastSavedSnapshot,
    setIsDesignDirty,
    getManualSaveHandler: () => null,
    setIsSaving,
    onRefetchSuccess: (fresh: any) => dispatch(grpDetailsActions.updateGroupData(fresh))
  });

  const handleSave = async () => {
    await executeSave();
  };

  // Learning Objectives are mentorship-only. Guided Course also has matches +
  // AI matching, just no learning objectives.
  const isMentorship = model === 'mentorship';
  const loading = basicSettingsLoading || learningObjectivesLoading;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 460,
          maxWidth: '92vw',
          bgcolor: '#0F0F0F',
          borderLeft: '1px solid #2A2A2A',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid #2A2A2A'
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#FDFDFD', fontFamily: 'Inter, sans-serif' }}>
            Match Settings
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#868686', fontFamily: 'Inter, sans-serif' }}>
            Learning objectives & matching rules
          </Typography>
        </Box>
        <CloseIcon onClick={onClose} sx={{ color: '#868686', cursor: 'pointer' }} />
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 2.5 }}>
        {loading ? (
          <LoadingSpinner minHeight={240} message="Loading settings..." />
        ) : (
          <>
            {isMentorship && (
              <>
                {/* Learning Objectives (mentorship only) */}
                <Box sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700, mb: 2 }}>Learning Objectives</Box>
                <Typography sx={{ color: '#B6B6B6', fontSize: 12, mb: 2, lineHeight: '18px' }}>
                  These entries will be displayed to {w('mentee', { plural: true, lower: true })} as Learning Objectives
                  and {w('mentor', { plural: true, lower: true })} as Areas of Expertise to help matchmaking, so please
                  use neutral language.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {learningObjectives.objectives.map((obj, idx) => (
                    <InputField
                      key={idx}
                      label={`Objective ${idx + 1}`}
                      value={obj}
                      placeholder={`Objective ${idx + 1}`}
                      onChange={(v) => {
                        markUserEdited();
                        setLearningObjectives((prev) => {
                          const next = [...prev.objectives];
                          next[idx] = v;
                          return { ...prev, objectives: next, number: next.length || 1 };
                        });
                      }}
                    />
                  ))}
                  <Button
                    variant="outlined"
                    onClick={() => {
                      markUserEdited();
                      setLearningObjectives((prev) => ({
                        number: (prev.objectives.length || 0) + 1,
                        objectives: [...prev.objectives, '']
                      }));
                    }}
                    sx={{
                      color: '#FDFDFD',
                      borderColor: '#565656',
                      textTransform: 'none',
                      fontSize: 12,
                      alignSelf: 'flex-start'
                    }}
                  >
                    Add Objective
                  </Button>
                  <InputField
                    label={`Required Learning Objectives for ${w('mentee', { plural: true })} *`}
                    value={`${learningObjectives.number || 0}`}
                    onChange={(v) => {
                      markUserEdited();
                      setLearningObjectives((prev) => ({ ...prev, number: Number(v) || 0 }));
                    }}
                  />
                </Box>
              </>
            )}

            {/* Matching (mentorship + guided course) */}
            <Box sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700, mb: 2, mt: isMentorship ? 3 : 0 }}>
              Matching
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <InputField
                label="Maximum matches per participant *"
                value={`${maxMatchesPerMentee}`}
                onChange={(v) => {
                  markUserEdited();
                  setMaxMatchesPerMentee(Number(v) || 0);
                }}
              />
              <InputField
                label={`Maximum matches per ${w('mentor', { lower: true })} *`}
                value={`${maxMatchesPerMentor}`}
                onChange={(v) => {
                  markUserEdited();
                  setMaxMatchesPerMentor(Number(v) || 0);
                }}
              />
              <InputField
                label="Required sessions per match *"
                value={`${sessionsPerMatch}`}
                onChange={(v) => {
                  markUserEdited();
                  setSessionsPerMatch(Number(v) || 0);
                }}
              />
            </Box>

            {/* AI Matching */}
            <Box sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700, mb: 2, mt: 3 }}>Matching with Elliott</Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                borderRadius: 1.5,
                border: '1px solid #2A2A2A',
                backgroundColor: useAIMatching ? '#F7942014' : 'transparent'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesomeIcon sx={{ fontSize: 18, color: useAIMatching ? '#F79420' : '#868686' }} />
                <Box>
                  <Typography sx={{ fontSize: 13, color: '#FDFDFD', fontWeight: 500 }}>
                    Enable Matching with Elliott
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#868686', lineHeight: 1.3 }}>
                    Let Elliott find optimal matches based on profiles and goals
                  </Typography>
                </Box>
              </Box>
              <Switch
                checked={useAIMatching}
                onChange={(_, checked) => {
                  markUserEdited();
                  setUseAIMatching(checked);
                }}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#F79420' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#F79420' }
                }}
              />
            </Box>
            {useAIMatching && (
              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, border: '1px solid #2A2A2A', backgroundColor: '#0F0F0F' }}>
                <Typography sx={{ fontSize: 12, color: '#B6B6B6', mb: 1 }}>
                  Fields Elliott considers in addition to{' '}
                  <strong style={{ color: '#FDFDFD' }}>{ALWAYS_ON_AI_FIELD_LABEL}</strong> (always on):
                </Typography>
                <Grid container spacing={0} columns={{ xs: 1, sm: 2 }}>
                  {AI_MATCHING_FIELD_OPTIONS.map((field) => (
                    <Grid item xs={1} key={field.key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!aiMatchingFields[field.key]}
                            onChange={(_, checked) => setAiMatchingField(field.key, checked)}
                            sx={{ color: '#868686', '&.Mui-checked': { color: '#F79420' } }}
                          />
                        }
                        label={<Typography sx={{ fontSize: 12, color: '#FDFDFD' }}>{field.label}</Typography>}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Fixed footer */}
      <Box
        sx={{
          flexShrink: 0,
          px: 3,
          py: 2,
          borderTop: '1px solid #2A2A2A',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5
        }}
      >
        <DosenButton tone="basic" onClick={onClose}>
          Cancel
        </DosenButton>
        <DosenButton
          tone="cta"
          onClick={handleSave}
          disabled={!hasPendingChanges}
          loading={isSaving}
          loadingText="Saving..."
          sx={{ px: 3 }}
        >
          Save Changes
        </DosenButton>
      </Box>
    </Drawer>
  );
};

export default MatchSettingsDrawer;
